import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch } from '@/lib/api';

/** Typed data access for media retention (Task 21). */

export interface RetentionWatchRow {
  eventId: number;
  eventName: string | null;
  eventType: string;
  endDate: string;
  retentionExpiryDate: string;
  daysRemaining: number;
  customerId: number;
  customerName: string;
  customerEmail: string | null;
  customerMobile: string;
  reminderCount: number;
  lastReminderAt: string | null;
  lastReminderStatus: string | null;
}

export interface RetentionSettings {
  retentionMonths: number;
  noticeText: string;
}

export function useRetentionWatch(range: { from?: string; to?: string }) {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const query = params.toString();

  return useQuery({
    queryKey: ['retention', 'watch', range.from ?? '', range.to ?? ''],
    queryFn: () => apiGet<RetentionWatchRow[]>(`/retention/watch${query ? `?${query}` : ''}`),
  });
}

export interface RetentionAlerts {
  windowDays: number;
  items: RetentionWatchRow[];
}

/**
 * FR-RET-004 — what the notification bell shows.
 *
 * Polled rather than pushed. The thing being watched is a date crossing a threshold, so it
 * changes at most once a day per event; a websocket for that would be machinery in search
 * of a problem. Five minutes is well inside the window it reports on, and `refetchOnFocus`
 * covers the case that actually matters — a tab left open overnight, picked up in the
 * morning, which is exactly when a studio starts its day.
 */
export function useRetentionAlerts() {
  return useQuery({
    queryKey: ['retention', 'alerts'],
    queryFn: () => apiGet<RetentionAlerts>('/retention/alerts'),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The notice text, read once and shared.
 *
 * The banner and the PDF template both read the same setting, so an Admin editing the
 * wording changes what a customer sees on screen and what is printed on their next document
 * together — a long stale time is fine, because it changes about once a year.
 */
export function useRetentionSettings() {
  return useQuery({
    queryKey: ['retention', 'settings'],
    queryFn: () => apiGet<RetentionSettings>('/retention/settings'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useUpdateRetentionSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { retentionMonths?: number; noticeText?: string }) =>
      apiPatch<{ retentionMonths: number; rebased: number; leftAlone: number }>(
        '/retention/settings',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retention'] });
      // Expiry dates may have moved, so anything showing one is now stale.
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/** Shared urgency banding, so the list, the badge and the event page agree. */
export function retentionTone(daysRemaining: number): {
  tone: 'danger' | 'warning' | 'neutral';
  label: string;
} {
  if (daysRemaining < 0) {
    return { tone: 'danger', label: `Expired ${Math.abs(daysRemaining)} days ago` };
  }
  if (daysRemaining === 0) return { tone: 'danger', label: 'Expires today' };
  if (daysRemaining <= 30) return { tone: 'warning', label: `${daysRemaining} days left` };
  return { tone: 'neutral', label: `${daysRemaining} days left` };
}
